/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Flex } from 'chakra-v3';

export interface ResponsiveConfig {
  base: number;
  md?: number;
  xl?: number;
}

export interface ResponsiveStringConfig {
  base?: string | number;
  md?: string | number;
  xl?: string | number;
}

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  /**
   * Number of cards visible at once.
   * Controls the width of items and the centering padding.
   * Can be a number or an object for responsive breakpoints.
   * Example: 2.4 means 2 full cards visible + 0.4 of the 3rd card.
   */
  visibleCount?: number | ResponsiveConfig;
  /**
   * Optional fixed width for items.
   * If provided, overrides the automatic width calculation based on visibleCount.
   * visibleCount is still used to calculate centering padding.
   */
  itemWidth?: string | number | ResponsiveStringConfig;
  gap?: number; // Gap in pixels
}

export const Carousel = <T,>({
  items,
  renderItem,
  className = '',
  visibleCount = { base: 1.4, md: 2.4, xl: 3.4 },
  itemWidth,
  gap = 24,
}: CarouselProps<T>) => {
  const [viewportWidth, setViewportWidth] = useState(1440);

  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  const config = useMemo(() => {
    const normalize = (val: number | undefined, fallback: number) => Math.max(1, val ?? fallback);

    let base: number, md: number, xl: number;

    if (typeof visibleCount === 'number') {
      const val = normalize(visibleCount, 1);
      base = val;
      md = val;
      xl = val;
    } else {
      base = normalize(visibleCount.base, 1);
      md = normalize(visibleCount.md, base);
      xl = normalize(visibleCount.xl, md);
    }

    // When viewport > 1440px and itemWidth is provided, calculate max full cards that fit
    // This logic preserves the density calculation of the 1440px constraint
    if (viewportWidth > 1440 && itemWidth) {
      const getMaxFullCards = (width: string | number | undefined): number => {
        if (!width) return xl;
        
        const cardWidthPx = typeof width === 'number' ? width : parseFloat(width.toString().replace('px', ''));
        if (isNaN(cardWidthPx)) return xl;
        
        // Calculate max full cards: (1440 + gap) / (cardWidth + gap)
        const maxCards = Math.floor((1440 + gap) / (cardWidthPx + gap));
        return Math.max(1, maxCards);
      };

      if (typeof itemWidth === 'number' || typeof itemWidth === 'string') {
        xl = getMaxFullCards(itemWidth);
      } else if (itemWidth.xl) {
        xl = getMaxFullCards(itemWidth.xl);
      } else if (itemWidth.md) {
        xl = getMaxFullCards(itemWidth.md);
      } else if (itemWidth.base) {
        xl = getMaxFullCards(itemWidth.base);
      }
    }

    return { base, md, xl };
  }, [visibleCount, viewportWidth, itemWidth, gap]);

  const widthConfig = useMemo(() => {
    if (!itemWidth) return { base: undefined, md: undefined, xl: undefined };

    if (typeof itemWidth === 'number' || typeof itemWidth === 'string') {
      return { base: itemWidth, md: itemWidth, xl: itemWidth };
    }

    return {
      base: itemWidth.base,
      md: itemWidth.md ?? itemWidth.base,
      xl: itemWidth.xl ?? itemWidth.md ?? itemWidth.base,
    };
  }, [itemWidth]);

  const getStyles = (count: number, gap: number, width?: string | number) => {
    const ic = Math.floor(count);

    // We constrain the card width calculation to a theoretical max container of 1440px
    // regardless of the actual viewport width.
    const containerRef = `min(100%, 1440px)`;

    // Card Width
    const cardWidthCalc =
      width !== undefined
        ? typeof width === 'number'
          ? `${width}px`
          : width
        : `calc((${containerRef} - (${gap}px * (${count} - 1))) / ${count})`;

    // Group Width = (CardWidth * ic) + (Gap * (ic - 1))
    const groupWidthCalc = `calc((${cardWidthCalc} * ${ic}) + (${gap}px * (${ic} - 1)))`;

    // Side Padding = (100% - GroupWidth) / 2
    // Here, 100% is the full viewport, so the group centers in the screen,
    // but because the group width is capped at the 1440px logic, 
    // the extra space becomes padding on the sides.
    const sidePaddingCalc = `calc((100% - ${groupWidthCalc}) / 2)`;

    return {
      cardWidth: cardWidthCalc,
      sidePadding: sidePaddingCalc,
    };
  };

  const responsiveStyles = useMemo(() => {
    const baseStyles = getStyles(config.base, gap, widthConfig.base);
    const mdStyles = getStyles(config.md, gap, widthConfig.md);
    const xlStyles = getStyles(config.xl, gap, widthConfig.xl);

    return {
      cardWidth: {
        base: baseStyles.cardWidth,
        md: mdStyles.cardWidth,
        xl: xlStyles.cardWidth,
      },
      padding: {
        base: baseStyles.sidePadding,
        md: mdStyles.sidePadding,
        xl: xlStyles.sidePadding,
      },
    };
  }, [config, widthConfig, gap]);

  return (
    // Removed maxW="1440px" and mx="auto" to allow the track to span full viewport
    <Box className={className} position="relative" w="full" overflow="hidden">
      <Flex
        className="carousel-track"
        overflowX="auto"
        scrollSnapType="x mandatory"
        w="full"
        gap={`${gap}px`}
        px={responsiveStyles.padding}
        scrollPaddingX={responsiveStyles.padding}
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
        }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            className="carousel-item"
            flexShrink={0}
            scrollSnapAlign="start"
            display="flex"
            flexDirection="column"
            w={responsiveStyles.cardWidth}
          >
            {renderItem(item, index)}
          </Box>
        ))}
      </Flex>
    </Box>
  );
};